import { useEffect, useMemo, useState } from 'react';
import {
  useMessageTab,
  useGetUser,
  useTeamInfo,
  useUserMetadata,
  useUserSettings,
} from 'src/hooks';
import {
  ChitChatCard,
  TaskSkill,
  MessageTabs,
  MessageTabData,
  GTMEvent,
  AiGenerationStatus,
  MessageSkill,
  Message,
  ArtifactType,
  TaskState,
  SingleSelectSetting,
  SettingKeys,
} from 'src/types';
import { prepareExternalModelsTabContent, sendGTMEvent } from 'src/utils';
import { Tabs, TabPanel } from 'src/v2/commonComponents/Tabs';
import { renderMessageTabList } from '../MessageTabList';
import styles from './ThreadChitChatCard.module.scss';

// TODO(olha): needs refactoring
import { ThreadMessageContent } from 'src/components/FlatAppearance/components/ThreadMessageContent';
import { UpgradeButton } from 'src/components/UpgradeButton';
import { ProgressBar } from 'src/components/FlatAppearance/components/ThreadCombinedCard/components/ProgressBar';
import { SuperAgentUpsell } from '../SuperAgentUpsell';
import {
  writerCoderModelsContainsThinking,
  NINJA_AGENT_TITLE,
  SUPER_AGENT_AS_EXTERNAL_MODEL,
} from 'src/constants';
import { FeedbackRating } from 'src/v2/components/ThreadMessage/components/FeedbackRating';
import { IconName } from 'src/components/Icon';
import { DownloadFileTool } from 'src/v2/components/Editor/components/DownloadFileTool';
import { MoveToEditorTool } from '../MoveToEditorTool';
import { ContinueAnsweringButton } from '../ContinueAnsweringButton';
import { ShareButton } from 'src/components/ShareButton';
import { MoreMessageActionsTool } from '../MoreMessageActionsTool/MoreMessageActionsTool';
import { RelatedQuestions } from 'src/components/RelatedQuestions';
import { CopyMessageTool } from '../CopyMessageTool';
import { CopyMessageButton } from '../CopyMessageButton';
import { DownloadPodcastButton } from '../DownloadPodcastButton';
import { BoostWithSNButton } from '../BoostWithSNButton';
import { ContextLimitModal } from 'src/components/ContextLimitModal';
import { useContextLimitModal } from 'src/hooks/useContextLimitModal';

interface ThreadChatCardProps {
  message: Message;
  messageId: string;
  chitChatCard: ChitChatCard;
  isStreaming?: boolean;
  messageSkill: MessageSkill | null;
  taskTitle: string;
  isLast: boolean;
  taskState?: TaskState;
  onSetActiveTab: (activeTab: string) => void;
}

const TEXT_MAX_SIZE = 150;

// TODO(olha): Tabs feature needs deep refactoring
export const ThreadChitChatCard = ({
  messageId,
  message,
  chitChatCard,
  isStreaming = false,
  messageSkill,
  taskTitle,
  isLast,
  taskState,
  onSetActiveTab,
}: ThreadChatCardProps) => {
  const { isOpenTier, isFreeTier, isTeamsStandardTier } = useGetUser();

  const { metadata } = useUserMetadata();
  const { setting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.WRITER_CODER_PRIMARY_MODEL,
  );

  const { isOwner } = useTeamInfo();
  const { changeShouldShowContextLimitModal } = useContextLimitModal();

  const { data } = chitChatCard;
  const { ninja_model_output, referenced_model_summary, skill } = data || {};

  const { references: external_models_result } = referenced_model_summary || {};

  const tabContent = useMemo(
    () =>
      external_models_result
        ? prepareExternalModelsTabContent({
            references: external_models_result,
            metadata,
          })
        : [],
    [external_models_result, metadata],
  );

  const superAgentTabData = useMemo(
    () =>
      tabContent.find(({ tabName }) =>
        tabName.includes(SUPER_AGENT_AS_EXTERNAL_MODEL),
      ),
    [tabContent],
  );

  const withSuperAgent = useMemo(
    () => !!superAgentTabData,
    [superAgentTabData],
  );

  const tabsData: MessageTabData[] = useMemo(
    () => [
      ...(ninja_model_output
        ? [
            {
              tabName: MessageTabs.NINJA_AGENT,
              iconName: 'ninjaOutlined' as IconName,
              tabTitle:
                !!message?.model_ids && !!metadata
                  ? metadata[message?.model_ids[0] || '']?.display_name || ''
                  : NINJA_AGENT_TITLE,
              content: ninja_model_output || '',
              status:
                tabContent.some(
                  ({ status }) => status === AiGenerationStatus.SUCCESS,
                ) || isStreaming === false
                  ? AiGenerationStatus.SUCCESS
                  : undefined,
            },
          ]
        : []),
      ...tabContent,
    ],
    [ninja_model_output, tabContent, isStreaming, message, metadata],
  );

  const { getTabSRegistry, onTabClick } = useMessageTab({
    messageId,
    tabsData,
  });

  const shouldDisableActions = useMemo(() => {
    if ((isOpenTier || isFreeTier) && skill === TaskSkill.DOCUMENT_QA) {
      return true;
    }

    if (isStreaming || taskState !== TaskState.DONE) {
      return true;
    }

    return false;
  }, [isOpenTier, isFreeTier, skill, isStreaming, taskState]);

  const [activeTab, setActiveTab] = useState<string>(
    tabsData[0]?.tabName || MessageTabs.NINJA_AGENT,
  );

  // TODO(olha): WIP Virtuoso
  useEffect(() => {
    if (tabsData[0]?.tabName) {
      setActiveTab(tabsData[0]?.tabName);
      onSetActiveTab(tabsData[0]?.tabName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsData[0]?.tabName]);

  useEffect(() => {
    if (
      data?.status === AiGenerationStatus.FAILURE &&
      ninja_model_output?.includes('Context limit reached')
    ) {
      changeShouldShowContextLimitModal(true);
    }
  }, [changeShouldShowContextLimitModal, data?.status, ninja_model_output]);

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    onTabClick(tabName);
    onSetActiveTab(tabName);

    if (tabName === MessageTabs.SUPER_AGENT) {
      sendGTMEvent(GTMEvent.SUPERAGENT_UPSELL_TAB_CLICK);
      return;
    }

    if (withSuperAgent && tabName === superAgentTabData?.tabName) {
      tabContent[0]?.content
        ? sendGTMEvent(GTMEvent.SUPERAGENT_COMPLETE_CLICK)
        : sendGTMEvent(GTMEvent.SUPERAGENT_IN_PROGRESS_CLICK);
      return;
    }
  };

  if (skill === TaskSkill.DOCUMENT_QA && (isOpenTier || isFreeTier)) {
    if (ninja_model_output) {
      const trimmedContent = ninja_model_output.replace(/^\n+|\n+$/g, '');
      const isLongText = trimmedContent.length > TEXT_MAX_SIZE;
      const slicedContent = `${trimmedContent
        .slice(0, TEXT_MAX_SIZE)
        .trim()}...`;
      return (
        <div>
          <div className={styles.contentWrapper}>
            <ThreadMessageContent
              content={slicedContent}
              messageSkill={messageSkill}
              isStreaming={isStreaming}
            />
            {isLongText && <div className={styles.blurOverlay} />}
          </div>
          {isLongText &&
            (!isTeamsStandardTier ? (
              <div className={styles.upsellBlockWrapper}>
                <span className={styles.upsellBlockText}>
                  Upgrade to reveal the answer and include your file(s) in
                  follow-up conversations.
                </span>
                <UpgradeButton
                  dataGTM="File-agent-response-upgrade"
                  title="Upgrade to unlock"
                />
              </div>
            ) : (
              <div className={styles.upgradeButtonWrapper}>
                <UpgradeButton
                  dataGTM="File-agent-response-upgrade"
                  title={
                    isOwner ? 'Upgrade to Unlock' : 'Request Upgrade to Unlock'
                  }
                />
              </div>
            ))}
        </div>
      );
    }

    return null;
  }

  const withThinking =
    message?.model_ids && message?.model_ids[0]
      ? writerCoderModelsContainsThinking.includes(message?.model_ids[0])
      : false;

  return (
    <div className={styles.root}>
      <ContextLimitModal />

      <Tabs<string> defaultActiveTab={activeTab} onTabClick={handleTabClick}>
        {tabsData.length > 1 &&
          renderMessageTabList({
            data: tabsData,
            withSuperAgent,
            tabRegistry: getTabSRegistry(),
          })}

        {tabsData.map(({ tabName, content, tabTitle, status, reasoning }) => {
          const isSuperAgentTab =
            tabName.includes(SUPER_AGENT_AS_EXTERNAL_MODEL) ||
            tabName === MessageTabs.SUPER_AGENT;

          const isTabStreaming =
            tabName === MessageTabs.NINJA_AGENT
              ? isStreaming
              : status !== AiGenerationStatus.SUCCESS;

          return (
            <TabPanel key={tabName} tab={tabName}>
              {content.length === 0 && !reasoning ? (
                // TODO(olha): create a common component
                <>
                  {(!isSuperAgentTab || withSuperAgent) && (
                    <>
                      <div className={styles.blinkingProgressText}>
                        Generating the response...
                      </div>
                      {!withThinking && <ProgressBar count={3} withColor />}
                    </>
                  )}

                  {isSuperAgentTab && !setting?.options[tabName].selectable && (
                    <SuperAgentUpsell description="An advanced agent that dives deep into your questions, leveraging relevant sources to deliver more accurate and richer answers." />
                  )}
                </>
              ) : (
                <div className={styles.contentWrapper}>
                  <ThreadMessageContent
                    messageSkill={messageSkill}
                    content={content}
                    isStreaming={isTabStreaming}
                    reasoning={reasoning}
                  />

                  {!isStreaming && (
                    <div className={styles.actionsWrapper}>
                      <BoostWithSNButton message={message} isDisabled={false} />

                      {isLast && (
                        <ContinueAnsweringButton
                          disabled={shouldDisableActions}
                        />
                      )}

                      {skill !== TaskSkill.CODING && (
                        <DownloadPodcastButton
                          message={message}
                          activeTab={activeTab}
                          disabled={shouldDisableActions}
                        />
                      )}

                      <DownloadFileTool
                        toolType="message"
                        message={message}
                        activeTab={activeTab}
                        disabled={shouldDisableActions}
                        temporarySkipPDF={skill === TaskSkill.CODING}
                        taskTitle={taskTitle}
                      />

                      {skill !== TaskSkill.CODING && (
                        <MoveToEditorTool
                          message={message}
                          activeTab={activeTab}
                          componentType={ArtifactType.TEXT}
                          disabled={taskState !== TaskState.DONE}
                          taskTitle={taskTitle}
                        />
                      )}

                      {messageSkill === MessageSkill.CODER ? (
                        <CopyMessageTool
                          message={message}
                          activeTab={activeTab}
                          disabled={shouldDisableActions}
                        />
                      ) : (
                        <CopyMessageButton
                          withTitle
                          withBackground
                          message={message}
                          activeTab={activeTab}
                          disabled={shouldDisableActions}
                          tooltipPlace="top"
                        />
                      )}

                      <ShareButton
                        withTitle
                        messageId={messageId}
                        disabled={shouldDisableActions}
                        tooltipPlace="top"
                      />

                      <MoreMessageActionsTool
                        message={message}
                        disabled={shouldDisableActions}
                      />
                    </div>
                  )}

                  <RelatedQuestions
                    message={message}
                    isStreaming={isStreaming}
                  />

                  <FeedbackRating
                    tabTitle={tabTitle}
                    message={message}
                    isStreaming={status !== AiGenerationStatus.SUCCESS}
                    operationType="models"
                    modelId={tabName}
                    messageSkill={messageSkill}
                  />
                </div>
              )}
            </TabPanel>
          );
        })}
      </Tabs>
    </div>
  );
};
